import React from "react";
import { NavLink as RouterNavLink, useNavigate, Outlet } from "react-router-dom";
import { useSuperAdmin } from "@/contexts/SuperAdminContext";
import { Building2, LogOut, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const NAV_ITEMS = [
  {
    label: "Accounts",
    path: "/superadmin/accounts",
    icon: Building2,
  },
  {
    label: "AI Usage",
    path: "/superadmin/ai-usage",
    icon: Bot,
  },
];

export default function SuperAdminLayout() {
  const { superAdmin, logout } = useSuperAdmin();
  const navigate = useNavigate();

  function handleLogout() {
    logout();
    navigate("/superadmin/login");
  }

  return (
    <div className="flex h-screen overflow-hidden bg-[#0d1117] text-[#e6edf3]">
      {/* Sidebar */}
      <aside className="w-60 flex-shrink-0 flex flex-col bg-[#161b22]/95 border-r border-white/[0.06] backdrop-blur-lg">
        {/* Logo area */}
        <div className="px-5 py-6 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <img
              src="/besthr.png"
              alt="BestHR"
              className="h-8 w-auto object-contain"
            />
            <div>
              <div className="text-sm font-bold text-[#e6edf3]">BestHR</div>
              <div className="text-[11px] text-white/35 mt-0.5">Admin Console</div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {NAV_ITEMS.map(item => (
            <RouterNavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  isActive
                    ? "bg-purple-600/20 text-white"
                    : "text-white/50 hover:text-white/80 hover:bg-white/5"
                )
              }
            >
              <item.icon className="h-[18px] w-[18px] flex-shrink-0" />
              {item.label}
            </RouterNavLink>
          ))}
        </nav>

        {/* User footer */}
        <div className="px-3 py-4 border-t border-white/[0.06]">
          <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-white/[0.03]">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
              {superAdmin?.name?.charAt(0)?.toUpperCase() || "S"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[#e6edf3] truncate">
                {superAdmin?.name || "Super Admin"}
              </div>
              <div className="text-[11px] text-white/35 mt-0.5">Super Admin</div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              title="Logout"
              className="h-7 w-7 text-white/30 hover:text-red-400 hover:bg-red-500/10 flex-shrink-0"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto bg-[#0d1117]">
        <Outlet />
      </main>
    </div>
  );
}
